import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldOff, Trash2, Plus, Upload, Loader2 } from "lucide-react";

export function JsaExemptions({ open, onClose }) {
  const [data, setData] = useState({ exemptions: [], known_employees: [] });
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleBulkUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/qms/jsa-exemptions/bulk", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Bulk upload done — ${r.data.added} added, ${r.data.skipped_existing} already on the list`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk upload failed");
    } finally { setUploading(false); }
  };

  const load = () => api.get("/qms/jsa-exemptions").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/qms/jsa-exemptions", { name: name.trim(), reason: reason.trim() || null });
      toast.success(`${name.trim()} excluded from Daily JSA & performance KPIs`);
      setName(""); setReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add exemption");
    } finally { setBusy(false); }
  };

  const remove = async (x) => {
    try {
      await api.delete(`/qms/jsa-exemptions/${x.id}`);
      toast.success(`${x.name} back in the Daily JSA & performance calc`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="jsa-exemptions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ShieldOff className="w-4 h-4 text-slate-500" /> Exempt Staff (Managers &amp; Apprentices)
          </DialogTitle>
          <DialogDescription className="text-xs">
            Staff on this list are excluded from the BU Performance measures — the
            Daily JSA (pre-start) KPI <b>and</b> Productive Utilisation, Timesheet Discipline
            and GP per Productive Hour. Add apprentices, BU managers and supervisors here.
            Mirrors the Power BI "Managers and Apprentices" SharePoint list.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input list="jsa-known-employees" value={name} placeholder="Employee name"
                 onChange={(e) => setName(e.target.value)} className="h-8 text-sm"
                 data-testid="jsa-exemption-name-input" />
          <datalist id="jsa-known-employees">
            {data.known_employees.map((k) => <option key={k} value={k} />)}
          </datalist>
          <Input value={reason} placeholder="Reason (e.g. Apprentice)"
                 onChange={(e) => setReason(e.target.value)} className="h-8 text-sm w-44"
                 data-testid="jsa-exemption-reason-input" />
          <Button size="sm" onClick={add} disabled={busy || !name.trim()}
                  className="h-8" data-testid="jsa-exemption-add-btn">
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-[11px] text-slate-400">
            Or bulk upload an xlsx/csv with Name + Classification columns
          </div>
          <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden"
                 onChange={handleBulkUpload} data-testid="jsa-exemption-bulk-file-input" />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}
                  disabled={uploading} className="h-7 text-xs gap-1"
                  data-testid="jsa-exemption-bulk-upload-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Bulk Upload
          </Button>
        </div>
        <div className="max-h-72 overflow-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
          {data.exemptions.length === 0 && (
            <div className="text-center text-sm text-slate-400 py-6">No exemptions yet.</div>
          )}
          {data.exemptions.map((x) => (
            <div key={x.id} className="flex items-center justify-between px-3 py-2 text-sm"
                 data-testid={`jsa-exemption-row-${x.id}`}>
              <div>
                <div className="font-medium text-slate-800">{x.name}</div>
                <div className="text-[11px] text-slate-400">
                  {x.reason || "—"} · added by {x.added_by || "?"}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => remove(x)}
                      className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600"
                      data-testid={`jsa-exemption-remove-${x.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
